import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Scissors, Play, Pause, RotateCcw, Check, Loader2 } from 'lucide-react';
import Hls from 'hls.js';

const API = process.env.REACT_APP_BACKEND_URL;

const VideoTrimmer = ({ videoUrl, videoTitle, videoId, cloudflareUid, category, onTrimComplete, onClose }) => {
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const timelineRef = useRef(null);

  // Initialize HLS or direct video
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;

    // Use HLS manifest for Cloudflare videos
    const hlsUrl = cloudflareUid
      ? `https://customer-vbbn7p39z8nnfjwk.cloudflarestream.com/${cloudflareUid}/manifest/video.m3u8`
      : null;

    if (hlsUrl && Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1 });
      hlsRef.current = hls;
      hls.loadSource(hlsUrl);
      hls.attachMedia(vid);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Force highest quality
        if (hls.levels && hls.levels.length > 0) {
          hls.currentLevel = hls.levels.length - 1;
        }
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          setError('Failed to load video stream. Trimming will still work with time inputs.');
        }
      });
    } else if (hlsUrl && vid.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      vid.src = hlsUrl;
    } else if (videoUrl) {
      vid.src = videoUrl;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [cloudflareUid, videoUrl]);

  // Handle video metadata loaded
  const handleLoadedMetadata = useCallback(() => {
    const vid = videoRef.current;
    if (vid && vid.duration && isFinite(vid.duration)) {
      setDuration(vid.duration);
      setEndTime(vid.duration);
      setVideoLoaded(true);
    }
  }, []);

  // Update current time during playback
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const handleTimeUpdate = () => {
      setCurrentTime(vid.currentTime);
      if (vid.currentTime >= endTime) {
        vid.pause();
        setIsPlaying(false);
      }
    };
    vid.addEventListener('timeupdate', handleTimeUpdate);
    return () => vid.removeEventListener('timeupdate', handleTimeUpdate);
  }, [endTime]);

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (isPlaying) {
      vid.pause();
      setIsPlaying(false);
    } else {
      if (vid.currentTime < startTime || vid.currentTime >= endTime) {
        vid.currentTime = startTime;
      }
      vid.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleTimelineClick = (e) => {
    if (!timelineRef.current || !duration) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    const time = pct * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleTrim = async () => {
    if (!cloudflareUid) {
      setError('No Cloudflare video ID available for trimming');
      return;
    }
    if (endTime - startTime < 1) {
      setError('Selection must be at least 1 second');
      return;
    }

    setProcessing(true);
    setProgress('Downloading video...');
    setError(null);

    try {
      const resp = await fetch(`${API}/api/videos/trim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          video_id: videoId,
          cloudflare_uid: cloudflareUid,
          start_time: startTime,
          end_time: endTime,
          title: `${videoTitle} (trimmed)`,
          category: category,
        })
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || 'Trim failed');
      }

      const data = await resp.json();
      setProgress('Complete!');

      if (onTrimComplete) {
        onTrimComplete(null, data.video);
      }
    } catch (err) {
      setError(err.message || 'Trimming failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const resetSelection = () => {
    setStartTime(0);
    setEndTime(duration);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const trimDuration = endTime - startTime;
  const startPct = duration > 0 ? (startTime / duration) * 100 : 0;
  const endPct = duration > 0 ? (endTime / duration) * 100 : 100;
  const currentPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="video-trimmer">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-lg">Trim Video</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full" data-testid="close-trimmer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Video preview */}
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
            <video
              ref={videoRef}
              className="w-full h-full object-contain"
              onLoadedMetadata={handleLoadedMetadata}
              onError={() => {
                if (!videoLoaded) {
                  setError('Failed to load video preview. Trimming will still work.');
                }
              }}
              playsInline
            />
            {!videoLoaded && !error && (
              <div className="absolute inset-0 flex items-center justify-center text-white/60">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}
          </div>

          {/* Play controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="p-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors"
              disabled={!videoLoaded}
              data-testid="play-pause-btn"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <span className="text-sm font-mono text-gray-600 min-w-[60px]">{formatTime(currentTime)}</span>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-mono text-gray-600">{formatTime(duration)}</span>
          </div>

          {/* Timeline with trim handles */}
          <div className="space-y-2">
            <div
              ref={timelineRef}
              className="relative h-12 bg-gray-200 rounded-lg cursor-pointer overflow-hidden"
              onClick={handleTimelineClick}
              data-testid="timeline"
            >
              {/* Selected range */}
              <div
                className="absolute top-0 bottom-0 bg-amber-400/30"
                style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
              />

              {/* Current time indicator */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                style={{ left: `${currentPct}%` }}
              />

              {/* Start handle */}
              <div
                className="absolute top-0 bottom-0 w-3 bg-green-500 cursor-ew-resize z-20 rounded-l"
                style={{ left: `${startPct}%` }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const rect = timelineRef.current.getBoundingClientRect();
                  const handleMove = (ev) => {
                    const x = ev.clientX - rect.left;
                    const pct = Math.max(0, Math.min(x / rect.width, endTime / duration - 0.01));
                    const newStart = pct * duration;
                    setStartTime(Math.max(0, newStart));
                  };
                  const handleUp = () => {
                    window.removeEventListener('mousemove', handleMove);
                    window.removeEventListener('mouseup', handleUp);
                  };
                  window.addEventListener('mousemove', handleMove);
                  window.addEventListener('mouseup', handleUp);
                }}
              />

              {/* End handle */}
              <div
                className="absolute top-0 bottom-0 w-3 bg-red-500 cursor-ew-resize z-20 rounded-r"
                style={{ left: `calc(${endPct}% - 12px)` }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const rect = timelineRef.current.getBoundingClientRect();
                  const handleMove = (ev) => {
                    const x = ev.clientX - rect.left;
                    const pct = Math.min(1, Math.max(x / rect.width, startTime / duration + 0.01));
                    const newEnd = pct * duration;
                    setEndTime(Math.min(duration, newEnd));
                  };
                  const handleUp = () => {
                    window.removeEventListener('mousemove', handleMove);
                    window.removeEventListener('mouseup', handleUp);
                  };
                  window.addEventListener('mousemove', handleMove);
                  window.addEventListener('mouseup', handleUp);
                }}
              />
            </div>

            {/* Time labels */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                Start: <span className="font-mono font-bold text-green-700">{formatTime(startTime)}</span>
              </div>
              <div className="text-center font-medium text-amber-600">
                Selection: {formatTime(trimDuration)}
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full" />
                End: <span className="font-mono font-bold text-red-700">{formatTime(endTime)}</span>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Processing status */}
          {processing && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
              <span className="text-amber-800 text-sm font-medium">{progress || 'Processing...'}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={resetSelection}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              disabled={processing}
              data-testid="reset-selection"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <div className="flex-1" />
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              onClick={handleTrim}
              className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium disabled:opacity-50"
              disabled={processing || trimDuration < 1}
              data-testid="trim-button"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {processing ? 'Trimming...' : 'Trim & Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTrimmer;
